import { useState } from "react";
import { useOrg } from "@/lib/orgContext";
import { useAuth } from "@clerk/react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle, Clock, Info, Loader2, Lock } from "lucide-react";

const STYLES: Record<string, { bg: string; border: string; text: string; iconBg: string; Icon: typeof Info }> = {
  trial: {
    bg: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-900",
    iconBg: "bg-blue-100 text-blue-700",
    Icon: Info,
  },
  trial_ending: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-900",
    iconBg: "bg-amber-100 text-amber-700",
    Icon: Clock,
  },
  past_due_grace: {
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-900",
    iconBg: "bg-red-100 text-red-700",
    Icon: AlertTriangle,
  },
  cancel_scheduled: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-900",
    iconBg: "bg-amber-100 text-amber-700",
    Icon: Clock,
  },
  read_only: {
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-900",
    iconBg: "bg-red-100 text-red-700",
    Icon: Lock,
  },
  cancelled: {
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-900",
    iconBg: "bg-red-100 text-red-700",
    Icon: Lock,
  },
};

export function LifecycleBanner() {
  const { org } = useOrg();
  const { getToken } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  if (!org?.lifecycle || org.lifecycle.state === "active") return null;

  const style = STYLES[org.lifecycle.state] ?? STYLES.trial;
  const { Icon } = style;

  async function handleCta() {
    if (!org?.lifecycle.cta) return;
    setLoading(true);
    try {
      const token = await getToken();
      const endpoint = org.lifecycle.cta.action === "billing_portal" ? "/api/billing/portal" : "/api/billing/checkout";
      const body = org.lifecycle.cta.action === "checkout" ? JSON.stringify({ plan: "track" }) : undefined;
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body,
      });
      const data = (await res.json()) as { url?: string; error?: string };
      if (!res.ok || !data.url) {
        toast({ title: "Could not open billing", description: data.error ?? "Please try again.", variant: "destructive" });
        return;
      }
      window.location.href = data.url;
    } catch {
      toast({ title: "Network error", description: "Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      data-testid="lifecycle-banner"
      data-state={org.lifecycle.state}
      className={`flex items-center gap-3 border-b ${style.bg} ${style.border} px-6 py-2.5 text-sm ${style.text}`}
    >
      <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full ${style.iconBg}`}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-medium leading-snug">{org.lifecycle.message}</p>
      </div>
      {org.lifecycle.cta && (
        <Button
          size="sm"
          variant={org.lifecycle.isReadOnly || org.lifecycle.state === "past_due_grace" ? "default" : "outline"}
          onClick={handleCta}
          disabled={loading}
          className={
            org.lifecycle.isReadOnly || org.lifecycle.state === "past_due_grace"
              ? "bg-[#0B1834] hover:bg-[#0A152E] text-white"
              : ""
          }
          data-testid="lifecycle-cta"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          {org.lifecycle.cta.label}
        </Button>
      )}
    </div>
  );
}
